/**
 * Amasty Multiple Wishlist compatibility for ScandiPWA
 * @copyright Scandiweb, Inc. All rights reserved.
 */

import PropTypes from 'prop-types';
import { PureComponent } from 'react';

import ClickOutside from 'Component/ClickOutside';
import Image from 'Component/Image';
import Link from 'Component/Link';

import { ItemType } from '../../type/MultipleWishlist.type';
import { getIsWishlistPopup } from '../../util/MultipleWishlist';
import { MAX_IMAGES_IN_WISHLIST } from '../MultipleWishlist/MultipleWishlist.config';

/** @namespace Scandiweb/AmastyMultipleWishlist/Component/MultipleWishlistItem/Component */
export class MultipleWishlistItemComponent extends PureComponent {
    static propTypes = {
        item: ItemType.isRequired,
        handleClick: PropTypes.func.isRequired,
        handleButtonMoreClick: PropTypes.func.isRequired,
        handleDelete: PropTypes.func.isRequired,
        isExpanded: PropTypes.bool.isRequired,
        isProductPage: PropTypes.bool.isRequired,
    };

    renderTitle() {
        const { item: { name } } = this.props;

        return (
            <p block="MultipleWishlistItem" elem="Name">
                { name }
            </p>
        );
    }

    // eslint-disable-next-line @scandipwa/scandipwa-guidelines/no-arrow-functions-in-class
    renderImage = (image, idx) => {
        const elem = getIsWishlistPopup() ? 'ProductImagePopup' : 'ProductImage';

        if (idx > MAX_IMAGES_IN_WISHLIST) {
            return null;
        }

        return (
        <div
          key={ idx + image }
          block="MultipleWishlistItem"
          elem={ elem }
        >
            <Image key={ idx } src={ image } alt="ProductImage" />
        </div>
        );
    };

    renderImages() {
        const { item: { images } } = this.props;

        return images.map(this.renderImage);
    }

    renderEmptyFolder() {
        const { item: { images } } = this.props;

        if (images.length) {
            return null;
        }

        return (
            <div block="MultipleWishlistItem" elem="EmptyMessage">
                { __('The folder is empty') }
            </div>
        );
    }

    renderPreviewBlockPopup() {
        const { item: { wishlistId } } = this.props;
        const link = `wishlists/${wishlistId}`;
        const elem = getIsWishlistPopup() ? 'PreviewPopup' : 'Preview';

        return (
            <div
              block="MultipleWishlistItem"
              elem={ elem }
            >
                <Link to={ link } />
                { this.renderImages() }
                { this.renderEmptyFolder() }
            </div>
        );
    }

    renderPreviewBlock() {
        const { item: { wishlistId }, handleClick } = this.props;
        const link = `wishlists/${wishlistId}`;
        const elem = getIsWishlistPopup() ? 'PreviewPopup' : 'Preview';

        return (
            <div
              block="MultipleWishlistItem"
              elem={ elem }
              onClick={ handleClick }
              onKeyDown={ null }
              role="button"
              tabIndex={ 0 }
            >
                <Link to={ link } />
                { this.renderImages() }
                { this.renderEmptyFolder() }
            </div>
        );
    }

    renderDesc() {
        const { item: { images } } = this.props;
        const desc = images.length === 1 ? `${images.length}${__(' wishlist item')}` : `${images.length}${__(' wishlist item')}`;

        return (
            <div block="MultipleWishlistItem" elem="Desc">
                { desc }
            </div>
        );
    }

    renderBlock() {
        const { isExpanded, isProductPage, handleButtonMoreClick } = this.props;

        if (!isExpanded || isProductPage) {
            return null;
        }

        return (
            <ClickOutside onClick={ handleButtonMoreClick }>
                <div block="MultipleWishlistItem" elem="Block">
                    { this.renderBlockView() }
                    { this.renderBlockDelete() }
                </div>
            </ClickOutside>
        );
    }

    renderBlockView() {
        const { handleClick } = this.props;

        return (
            <button
              block="MultipleWishlistItem"
              elem="ViewItem"
              onClick={ handleClick }
            >
              { __('View') }
            </button>
        );
    }

    renderBlockDelete() {
        const { handleDelete, item: { isDeleteable } } = this.props;

        if (!isDeleteable) {
            return null;
        }

        return (
            <button
              block="MultipleWishlistItem"
              elem="DeleteItem"
              onClick={ handleDelete }
            >
              { __('Delete') }
            </button>
        );
    }

    renderButtonMore() {
        const { handleButtonMoreClick, isProductPage } = this.props;

        if (getIsWishlistPopup() || isProductPage) {
            return null;
        }

        return (
            <button
              block="MultipleWishlistItem"
              elem="ButtonMore"
              title="Open List Of Actions"
              aria-label="hidden"
              onClick={ handleButtonMoreClick }
            />
        );
    }

    renderAddToCart() {
        const { handleClick, isProductPage } = this.props;

        if (getIsWishlistPopup() || isProductPage) {
            return null;
        }

        return (
            <button
              block="Button"
              mix={ { block: 'MultipleWishlistItem', elem: 'AddToCartButton' } }
              onClick={ handleClick }
            >
              { __('Add to Cart') }
            </button>
        );
    }

    renderSection() {
        return (
            <section block="MultipleWishlistItem" elem="Section">
                { this.renderTitle() }
                { this.renderButtonMore() }
                { this.renderBlock() }
            </section>
        );
    }

    render() {
        const { handleClick } = this.props;

        if (getIsWishlistPopup()) {
            return (
                <div
                  block="MultipleWishlistItemPopup"
                  onClick={ handleClick }
                  onKeyDown={ null }
                  role="button"
                  tabIndex={ 0 }
                >
                    { this.renderPreviewBlockPopup() }
                    { this.renderSection() }
                    { this.renderDesc() }
                </div>
            );
        }

        return (
            <div block="MultipleWishlistItem">
                { this.renderPreviewBlock() }
                { this.renderSection() }
                { this.renderDesc() }
            </div>
        );
    }
}

export default MultipleWishlistItemComponent;
